import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import api from "@/lib/api";
import { tabClass } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Leaf } from "lucide-react";
import { renderMarkdown } from "@/lib/markdown";

const fmt = (d) => { try { return new Date(d).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" }); } catch { return ""; } };

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cat, setCat] = useState("All");
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false)); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set(posts.map((p) => p.category).filter(Boolean)))], [posts]);
  const shown = cat === "All" ? posts : posts.filter((p) => p.category === cat);

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="bg-emerald-950 text-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
          <span className="text-xs uppercase tracking-[0.3em] text-amber-300/90">The PlantFlow Journal</span>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Growing notes &amp; landcare stories</h1>
          <p className="mt-3 text-emerald-100/80 max-w-2xl">Seasonal tips, product updates, and customer stories from the PlantFlow team.</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-12">
        {categories.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8" data-testid="blog-category-filter">
            {categories.map((c) => (
              <button key={c} data-testid={`blog-cat-${c}`} onClick={() => setCat(c)} className={tabClass(cat === c)}>{c}</button>
            ))}
          </div>
        )}
        {loading && <p className="text-stone-400 text-sm">Loading…</p>}
        {!loading && shown.length === 0 && (
          <div data-testid="blog-empty" className="text-center py-20">
            <Leaf className="w-8 h-8 text-emerald-300 mx-auto" />
            <p className="mt-3 text-stone-500">No posts here yet — check back soon.</p>
          </div>
        )}
        <div className="space-y-8" data-testid="blog-list">
          {shown.map((p) => (
            <Link key={p.id} to={`/blog/${p.id}`} data-testid={`blog-post-${p.id}`}
              className="block rounded-3xl border border-black/5 bg-white overflow-hidden shadow-[0_8px_32px_rgba(0,0,0,0.04)] hover:border-emerald-300 transition-colors">
              {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-56 object-cover" />}
              <div className="p-8">
                <div className="flex items-center gap-3">
                  <Badge className="rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200">{p.category}</Badge>
                  <span className="text-xs text-stone-400">{fmt(p.date)}</span>
                </div>
                <h2 className="mt-3 font-serif text-3xl text-emerald-950">{p.title}</h2>
                <div className="mt-3 text-stone-600 leading-relaxed line-clamp-3 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: renderMarkdown(p.body) }} />
                <span className="mt-4 inline-block text-sm text-emerald-700 font-medium">Read more →</span>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
